import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './ColorsPage.css';

const ColorsPage = () => {
  const { token } = useAuth();
  const [colors, setColors] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandsWithAvailability, setBrandsWithAvailability] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Фильтры
  const [selectedBrand, setSelectedBrand] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  
  // Модалка для просмотра похожих цветов
  const [selectedColor, setSelectedColor] = useState(null);
  const [similarColors, setSimilarColors] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  
  // Модалка для пересчета всех похожих цветов
  const [showRecalcModal, setShowRecalcModal] = useState(false);
  const [recalcThreshold, setRecalcThreshold] = useState(10.0);
  const [recalculating, setRecalculating] = useState(false);
  
  // Модалка для импорта цветов
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFiles, setImportFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Модалка для управления брендами
  const [showBrandsModal, setShowBrandsModal] = useState(false);
  const [updatingBrand, setUpdatingBrand] = useState(null);

  // Массовое выделение цветов
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedColorIds, setSelectedColorIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedBrand, searchQuery, page]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        skip: page * limit,
        limit: limit
      };
      
      if (selectedBrand) params.brand = selectedBrand;
      if (searchQuery) params.search = searchQuery;
      
      const [colorsData, brandsData, statsData] = await Promise.all([
        api.get('/colors', { params }),
        api.get('/colors/brands'),
        api.get('/colors/stats')
      ]);
      
      setColors(colorsData.data);
      setBrands(brandsData.data);
      setStats(statsData.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBrandsWithAvailability = async () => {
    try {
      const response = await api.get('/colors/brands-with-availability');
      setBrandsWithAvailability(response.data);
    } catch (err) {
      console.error('Error loading brands availability:', err);
    }
  };

  const handleToggleBrandAvailability = async (brand, currentValue) => {
    setUpdatingBrand(brand);
    try {
      await api.put(`/colors/brands/${encodeURIComponent(brand)}/availability`, null, {
        params: { available_at_us: !currentValue }
      });
      // Перезагружаем список брендов
      await loadBrandsWithAvailability();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    } finally {
      setUpdatingBrand(null);
    }
  };

  // ─── Массовое выделение ──────────────────────────────────────────
  const toggleSelectionMode = () => {
    setSelectionMode(prev => {
      if (prev) setSelectedColorIds(new Set()); // выходим — очищаем выбор
      return !prev;
    });
  };

  const toggleColorSelection = (colorId) => {
    setSelectedColorIds(prev => {
      const next = new Set(prev);
      if (next.has(colorId)) {
        next.delete(colorId);
      } else {
        next.add(colorId);
      }
      return next;
    });
  };

  const allOnPageSelected = colors.length > 0 && colors.every(c => selectedColorIds.has(c.id));

  const toggleSelectAllOnPage = () => {
    setSelectedColorIds(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        colors.forEach(c => next.delete(c.id));
      } else {
        colors.forEach(c => next.add(c.id));
      }
      return next;
    });
  };

  const selectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      const params = {};
      if (selectedBrand) params.brand = selectedBrand;
      if (searchQuery) params.search = searchQuery;

      const response = await api.get('/colors/all-ids', { params });
      setSelectedColorIds(new Set(response.data.ids));
    } catch (err) {
      alert('Ошибка выбора всех цветов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => setSelectedColorIds(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedColorIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Удалить выбранные цвета (${ids.length} шт.)? Это действие необратимо.`)) return;

    setBulkDeleting(true);
    try {
      const response = await api.post('/colors/bulk-delete', { ids });
      alert(`✓ Удалено цветов: ${response.data.deleted}`);
      setSelectedColorIds(new Set());
      setSelectionMode(false);
      loadData();
    } catch (err) {
      alert('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleColorClick = async (color) => {
    setSelectedColor(color);
    setLoadingSimilar(true);
    
    try {
      const response = await api.get(`/colors/${color.id}/similarities`);
      setSimilarColors(response.data);
    } catch (err) {
      console.error('Error loading similar colors:', err);
      setSimilarColors([]);
    } finally {
      setLoadingSimilar(false);
    }
  };

  const handleRecalculateSimilarities = async (colorId) => {
    if (!window.confirm('Пересчитать похожие цвета для этого цвета?')) return;
    
    try {
      await api.post(`/colors/${colorId}/find-similar`, null, {
        params: { threshold: 10, limit: 50, auto_save: true }
      });
      alert('Похожие цвета успешно пересчитаны');
      handleColorClick(selectedColor); // Перезагружаем
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleToggleSimilarity = async (similarity, action) => {
    try {
      await api.put(`/colors/similarities/${similarity.id}`, {
        is_manually_approved: action === 'approve',
        is_manually_rejected: action === 'reject'
      });
      
      // Перезагружаем похожие цвета
      handleColorClick(selectedColor);
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleDeleteSimilarity = async (similarityId) => {
    if (!window.confirm('Удалить эту связь?')) return;
    
    try {
      await api.delete(`/colors/similarities/${similarityId}`);
      handleColorClick(selectedColor); // Перезагружаем
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleDeleteColor = async (colorId) => {
    if (!window.confirm('Удалить этот цвет? Будут удалены все связи с похожими цветами.')) return;
    
    try {
      await api.delete(`/colors/${colorId}`);
      alert('✓ Цвет успешно удален');
      closeModal();
      loadData(); // Обновляем список
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleRecalculateAll = async () => {
    setRecalculating(true);
    
    try {
      await api.post('/colors/recalculate-all-similarities', null, {
        params: { threshold: recalcThreshold }
      });
      alert(`✓ Похожие цвета успешно пересчитаны с порогом ΔE < ${recalcThreshold}`);
      setShowRecalcModal(false);
      loadData(); // Обновляем статистику
    } catch (err) {
      alert('Ошибка: ' + err.message);
    } finally {
      setRecalculating(false);
    }
  };

  const handleImportColors = async () => {
    if (importFiles.length === 0) {
      alert('Выберите хотя бы один JSON файл');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      importFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await api.post('/colors/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      loadData(); // Обновляем данные
    } catch (err) {
      alert('Ошибка импорта: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelect = (e) => {
    setImportFiles(Array.from(e.target.files));
    setImportResult(null);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFiles([]);
    setImportResult(null);
  };

  const closeModal = () => {
    setSelectedColor(null);
    setSimilarColors([]);
  };

  if (loading && colors.length === 0) {
    return <div className="loading">Загрузка цветов...</div>;
  }

  return (
    <div className="colors-page">
      <div className="page-header">
        <h1>Управление цветами</h1>
        <div className="stats">
          <span>Всего цветов: <strong>{stats.total_colors || 0}</strong></span>
          <span>Брендов: <strong>{stats.brands_count || 0}</strong></span>
          <span>Связей похожести: <strong>{stats.total_similarities || 0}</strong></span>
        </div>
        <div className="header-actions">
          <button 
            className="btn-brands"
            onClick={() => {
              loadBrandsWithAvailability();
              setShowBrandsModal(true);
            }}
          >
            🏷️ Бренды
          </button>
          <button 
            className={selectionMode ? 'btn-select-mode active' : 'btn-select-mode'}
            onClick={toggleSelectionMode}
          >
            {selectionMode ? '✖️ Отменить выбор' : '☑️ Выбрать'}
          </button>
          <button 
            className="btn-import"
            onClick={() => setShowImportModal(true)}
          >
            📥 Импортировать цвета
          </button>
          <button 
            className="btn-recalculate-all"
            onClick={() => setShowRecalcModal(true)}
          >
            🔄 Пересчитать похожие
          </button>
        </div>
      </div>

      <div className="filters">
        <select 
          value={selectedBrand} 
          onChange={(e) => { setSelectedBrand(e.target.value); setPage(0); }}
          className="filter-select"
        >
          <option value="">Все бренды</option>
          {brands.map(brand => (
            <option key={brand} value={brand}>{brand}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Поиск по названию, коду, hex..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          className="search-input"
        />
      </div>

      {error && <div className="error">Ошибка: {error}</div>}

      {selectionMode && (
        <div className="bulk-toolbar">
          <label className="bulk-select-all">
            <input
              type="checkbox"
              checked={allOnPageSelected}
              onChange={toggleSelectAllOnPage}
            />
            <span>Выбрать на странице</span>
          </label>
          <button
            className="btn-select-all-filtered"
            onClick={selectAllFiltered}
            disabled={selectingAll}
          >
            {selectingAll
              ? 'Загрузка...'
              : (selectedBrand || searchQuery)
                ? 'Выбрать всё по фильтру'
                : `Выбрать всё (${stats.total_colors || 0})`}
          </button>
          <span className="bulk-count">
            Выбрано: <strong>{selectedColorIds.size}</strong>
          </span>
          <div className="bulk-actions">
            <button
              className="btn-clear-selection"
              onClick={clearSelection}
              disabled={selectedColorIds.size === 0 || bulkDeleting}
            >
              Сбросить
            </button>
            <button
              className="btn-bulk-delete"
              onClick={handleBulkDelete}
              disabled={selectedColorIds.size === 0 || bulkDeleting}
            >
              {bulkDeleting ? 'Удаление...' : `🗑️ Удалить выбранные (${selectedColorIds.size})`}
            </button>
          </div>
        </div>
      )}

      <div className="colors-grid">
        {colors.map(color => {
          const isSelected = selectedColorIds.has(color.id);
          return (
            <div 
              key={color.id} 
              className={`color-card${selectionMode ? ' selectable' : ''}${isSelected ? ' selected' : ''}`}
              onClick={() => selectionMode ? toggleColorSelection(color.id) : handleColorClick(color)}
            >
              {selectionMode && (
                <input
                  type="checkbox"
                  className="color-card-checkbox"
                  checked={isSelected}
                  onChange={(e) => { e.stopPropagation(); toggleColorSelection(color.id); }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <div 
                className="color-preview" 
                style={{ backgroundColor: color.hex }}
                title={color.hex}
              />
              <div className="color-info">
                <div className="color-name">{color.name}</div>
                <div className="color-brand">{color.brand}</div>
                {color.code && <div className="color-code">{color.code}</div>}
                <div className="color-hex">{color.hex}</div>
              </div>
            </div>
          );
        })}
      </div>

      {colors.length === limit && (
        <div className="pagination">
          <button 
            disabled={page === 0} 
            onClick={() => setPage(p => p - 1)}
          >
            ← Назад
          </button>
          <span>Страница {page + 1}</span>
          <button onClick={() => setPage(p => p + 1)}>
            Вперёд →
          </button>
        </div>
      )}

      {/* Модалка с похожими цветами */}
      {selectedColor && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            
            <div className="modal-header">
              <div className="selected-color-display">
                <div 
                  className="large-color-preview" 
                  style={{ backgroundColor: selectedColor.hex }}
                />
                <div className="color-details">
                  <h2>{selectedColor.name}</h2>
                  <p>{selectedColor.brand} {selectedColor.code && `• ${selectedColor.code}`}</p>
                  <p className="hex-large">{selectedColor.hex}</p>
                  
                  <div className="color-params">
                    <div className="param-group">
                      <strong>RGB:</strong> 
                      <span>({selectedColor.rgb.r}, {selectedColor.rgb.g}, {selectedColor.rgb.b})</span>
                    </div>
                    
                    {selectedColor.hsv && (
                      <div className="param-group">
                        <strong>HSV:</strong> 
                        <span>
                          H: {selectedColor.hsv.h.toFixed(1)}° 
                          S: {selectedColor.hsv.s.toFixed(1)}% 
                          V: {selectedColor.hsv.v.toFixed(1)}%
                        </span>
                      </div>
                    )}
                    
                    {selectedColor.lab && (
                      <div className="param-group">
                        <strong>LAB:</strong> 
                        <span>
                          L: {selectedColor.lab.l.toFixed(2)} 
                          a: {selectedColor.lab.a.toFixed(2)} 
                          b: {selectedColor.lab.b.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <button 
                className="btn-recalculate"
                onClick={() => handleRecalculateSimilarities(selectedColor.id)}
              >
                🔄 Пересчитать похожие
              </button>
            </div>

            <div className="modal-body">
              <h3>Похожие цвета (Delta E &lt; 10)</h3>
              
              {loadingSimilar ? (
                <div className="loading">Загрузка...</div>
              ) : similarColors.length === 0 ? (
                <div className="no-similar">Похожие цвета не найдены. Нажмите "Пересчитать похожие".</div>
              ) : (
                <div className="similar-colors-list">
                  {similarColors.map(sim => (
                  <div key={sim.id} className={`similar-color-item ${
                    sim.is_manually_approved ? 'approved' : 
                    sim.is_manually_rejected ? 'rejected' : ''
                  }`}>
                    <div 
                      className="similar-color-preview" 
                      style={{ backgroundColor: sim.similar_color.hex }}
                    />
                    <div className="similar-color-info">
                      <div><strong>{sim.similar_color.name}</strong></div>
                      <div className="similar-color-meta">
                        {sim.similar_color.brand} • {sim.similar_color.hex}
                      </div>
                      <div className="delta-e">
                        ΔE: <strong>{sim.delta_e.toFixed(2)}</strong>
                        {sim.delta_e < 2.3 && ' (незаметная разница)'}
                        {sim.delta_e >= 2.3 && sim.delta_e < 5 && ' (близкие)'}
                        {sim.delta_e >= 5 && ' (похожие)'}
                      </div>
                    </div>
                    <div className="similar-actions">
                      {!sim.is_manually_approved && (
                        <button 
                          className="btn-approve"
                          onClick={() => handleToggleSimilarity(sim, 'approve')}
                          title="Одобрить как похожий"
                        >
                          ✓
                        </button>
                      )}
                      {!sim.is_manually_rejected && (
                        <button 
                          className="btn-reject"
                          onClick={() => handleToggleSimilarity(sim, 'reject')}
                          title="Отклонить"
                        >
                          ✗
                        </button>
                      )}
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteSimilarity(sim.id)}
                        title="Удалить связь"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-delete-color"
                onClick={() => handleDeleteColor(selectedColor.id)}
                title="Удалить цвет и все его связи"
              >
                🗑️ Удалить этот цвет
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка для импорта цветов */}
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeImportModal}>×</button>
            
            <h2>Импортировать цвета из JSON</h2>
            
            <div className="import-form">
              <div className="file-upload-area">
                <label htmlFor="file-input" className="file-upload-label">
                  <div className="upload-icon">📁</div>
                  <div className="upload-text">
                    {importFiles.length === 0 ? (
                      <>Выберите JSON файлы для импорта</>
                    ) : (
                      <>
                        Выбрано файлов: <strong>{importFiles.length}</strong>
                        <div className="selected-files">
                          {importFiles.map((f, i) => (
                            <div key={i} className="file-name">{f.name}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </label>
                <input
                  id="file-input"
                  type="file"
                  accept=".json"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
              
              <div className="format-hint">
                <p><strong>Формат JSON:</strong></p>
                <pre>{`[
  {
    "brand": "Kelly Moore",
    "code": "km4883",
    "name": "Black Cat",
    "hex": "#1a1a1a",
    "rgb": { "r": 26, "g": 26, "b": 26 }
  }
]`}</pre>
              </div>
              
              {importResult && (
                <div className="import-result">
                  <h3>Результаты импорта:</h3>
                  <div className="result-summary">
                    <div className="result-item success">
                      <span className="result-label">✓ Добавлено:</span>
                      <span className="result-value">{importResult.colors_added}</span>
                    </div>
                    <div className="result-item info">
                      <span className="result-label">⟳ Обновлено:</span>
                      <span className="result-value">{importResult.colors_updated}</span>
                    </div>
                    <div className="result-item warning">
                      <span className="result-label">⊘ Пропущено:</span>
                      <span className="result-value">{importResult.colors_skipped}</span>
                    </div>
                  </div>
                  
                  {importResult.details_by_file && importResult.details_by_file.length > 0 && (
                    <div className="file-details">
                      <h4>По файлам:</h4>
                      {importResult.details_by_file.map((fileDetail, idx) => (
                        <div key={idx} className="file-detail">
                          <strong>{fileDetail.filename}</strong>
                          {fileDetail.error ? (
                            <span className="error-text"> — Ошибка: {fileDetail.error}</span>
                          ) : (
                            <span className="detail-counts">
                              {' '}— добавлено: {fileDetail.added}, обновлено: {fileDetail.updated}, пропущено: {fileDetail.skipped}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="import-errors">
                      <h4>Ошибки:</h4>
                      {importResult.errors.map((err, idx) => (
                        <div key={idx} className="error-message">{err}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="modal-actions">
                <button 
                  className="btn-cancel"
                  onClick={closeImportModal}
                  disabled={importing}
                >
                  {importResult ? 'Закрыть' : 'Отмена'}
                </button>
                {!importResult && (
                  <button 
                    className="btn-primary"
                    onClick={handleImportColors}
                    disabled={importing || importFiles.length === 0}
                  >
                    {importing ? 'Импортируем...' : 'Импортировать'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модалка для пересчета всех похожих цветов */}
      {showRecalcModal && (
        <div className="modal-overlay" onClick={() => setShowRecalcModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRecalcModal(false)}>×</button>
            
            <h2>Пересчитать все похожие цвета</h2>
            
            <div className="recalc-form">
              <label>
                Порог Delta E (ΔE):
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="50"
                  value={recalcThreshold}
                  onChange={(e) => setRecalcThreshold(parseFloat(e.target.value))}
                  className="threshold-input"
                />
              </label>
              
              <div className="threshold-hint">
                <p>Рекомендуемые значения:</p>
                <ul>
                  <li><strong>&lt; 1.0</strong> — практически идентичные цвета</li>
                  <li><strong>&lt; 2.3</strong> — незаметная разница для глаза</li>
                  <li><strong>&lt; 5.0</strong> — близкие цвета</li>
                  <li><strong>&lt; 10.0</strong> — похожие цвета (по умолчанию)</li>
                </ul>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn-cancel"
                  onClick={() => setShowRecalcModal(false)}
                  disabled={recalculating}
                >
                  Отмена
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleRecalculateAll}
                  disabled={recalculating}
                >
                  {recalculating ? 'Пересчитываем...' : 'Пересчитать'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модалка для управления брендами */}
      {showBrandsModal && (
        <div className="modal-overlay" onClick={() => setShowBrandsModal(false)}>
          <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowBrandsModal(false)}>×</button>
            
            <h2>Управление брендами</h2>
            <p className="brands-hint">Отметьте бренды, колеровка которых доступна у вас</p>
            
            <div className="brands-list">
              {brandsWithAvailability.length === 0 ? (
                <div className="loading">Загрузка брендов...</div>
              ) : (
                brandsWithAvailability.map(brand => (
                  <div key={brand.brand} className="brand-item">
                    <label className="brand-checkbox">
                      <input
                        type="checkbox"
                        checked={brand.available_at_us}
                        onChange={() => handleToggleBrandAvailability(brand.brand, brand.available_at_us)}
                        disabled={updatingBrand === brand.brand}
                      />
                      <span className="brand-name">{brand.brand}</span>
                      <span className="brand-count">({brand.colors_count} цветов)</span>
                    </label>
                    {updatingBrand === brand.brand && (
                      <span className="updating-indicator">⟳</span>
                    )}
                  </div>
                ))
              )}
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-primary"
                onClick={() => setShowBrandsModal(false)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorsPage;
